import { useState, useRef, useCallback, useEffect } from 'react';
import { getToken } from '../lib/auth.js';
import { BFF_WS_BASE as WS_BASE } from '../lib/runtimeEnv.js';
import { useTranslation } from '../i18n';
import { getActiveClinicRef } from '../lib/clinicSignal.js';
import { watchSilence } from './silenceDetector.js';

/**
 * Server-side speech-to-text: records with MediaRecorder (works in every modern
 * browser, incl. Firefox) and streams the audio to the clinic BFF's
 * /ws/transcribe WebSocket, which relays it to the AI assistant's transcription.
 *
 * Exposes the SAME shape as useVoiceInput (browser Web Speech) so callers are
 * interchangeable: { status, transcript, errorDetail, start, stop, reset, isSupported }.
 */
function pickMime() {
  // `audio/mp4` is the only one Safari/iOS (before 18.4) accepts. Without it the
  // recorder still falls back to its own default, but naming it keeps the
  // choice explicit rather than accidental.
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  try {
    return types.find(type => window.MediaRecorder?.isTypeSupported?.(type)) || '';
  } catch {
    return '';
  }
}

// How long «پایان» waits for the server's FINAL transcript.
//
// The server path has no local text: interim words arrive only on the AI
// service's tick, and the first tick fires HINT_INITIAL_DELAY seconds into the
// recording (15s on beta1). A registration dictation is usually shorter than
// that, so at the moment «پایان» is pressed the only transcript that will ever
// exist is the one still on its way. Reading `getTranscript()` in that tick —
// which is what the form did — returned "" and showed «چیزی شنیده نشد» for
// speech the service had in fact transcribed. The doctor app waits 120s for the
// same message; one short clip's final pass needs far less, but not 6s.
export const FINAL_TRANSCRIPT_TIMEOUT_MS = 30000;

/**
 * `onSilence` / `onNoSpeech` — called when a recording started with
 * `start({ autoStop: true })` hears its answer end, or hears nothing at all
 * (silenceDetector.js). `onAutoStopUnavailable` — the browser would not let the
 * level be read, so nothing will end the recording by itself. `onLevel(rms)` —
 * ten times a second while a recording started with `{ meter: true }` (or
 * `autoStop`) runs: the microphone's level, 0..1, for a meter.
 */
export function useServerVoiceInput({
  continuous = false, onFinalSegment, onSilence, onNoSpeech, onAutoStopUnavailable, onLevel,
} = {}) {
  const { t } = useTranslation();
  const [status, setStatus]           = useState('idle');
  const [transcript, setTranscript]   = useState('');
  const [errorDetail, setErrorDetail] = useState('');

  const wsRef      = useRef(null);
  const mrRef      = useRef(null);
  const streamRef  = useRef(null);
  const accRef     = useRef('');
  const interimRef = useRef('');   // latest interim delta, not yet in accRef
  const finishRef  = useRef(null); // fallback timer to force-close the socket
  const startingRef = useRef(false); // guards against opening a second socket
  const finalResolveRef = useRef(null); // a pending finishTranscript() promise
  // Audio chunks go out IN ORDER, each after the previous one's arrayBuffer()
  // resolved. `done` is chained onto the same queue, so the recorder's last
  // chunk — emitted asynchronously by MediaRecorder.stop() — cannot land after
  // the server has already been told the recording is over.
  const sendChainRef = useRef(Promise.resolve());
  // The silence watch of an auto-stopping recording, and the callers' latest handlers.
  const vadRef = useRef(null);
  // Which start() is current. A teardown while start() still awaits the microphone
  // makes that start stale: without this, the late stream would open a recording
  // nobody is listening to — and the BFF would bill it.
  const genRef = useRef(0);
  const handlersRef = useRef({});
  handlersRef.current = { onSilence, onNoSpeech, onAutoStopUnavailable, onLevel };

  const stopSilenceWatch = useCallback(() => {
    if (vadRef.current) { vadRef.current.stop(); vadRef.current = null; }
  }, []);

  // Hard teardown: stop recorder/mic, clear the timer, and CLOSE the socket.
  const teardown = useCallback(() => {
    genRef.current += 1;
    startingRef.current = false;
    stopSilenceWatch();
    // Settle a pending «پایان» with whatever arrived. Every exit — final message,
    // server error, socket close, timeout, unmount — passes through here, so a
    // caller awaiting finishTranscript() is never left hanging.
    if (finalResolveRef.current) {
      const settle = finalResolveRef.current;
      finalResolveRef.current = null;
      settle((accRef.current + interimRef.current).trim());
    }
    if (finishRef.current) { clearTimeout(finishRef.current); finishRef.current = null; }
    try { if (mrRef.current && mrRef.current.state !== 'inactive') mrRef.current.stop(); } catch { /* ignore */ }
    try { streamRef.current?.getTracks().forEach(track => track.stop()); } catch { /* ignore */ }
    mrRef.current = null;
    streamRef.current = null;
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      try { ws.onmessage = ws.onerror = ws.onclose = null; } catch { /* ignore */ }
      try { ws.close(1000, 'client done'); } catch { /* ignore */ }
    }
  }, [stopSilenceWatch]);

  // User pressed پایان/stop: stop capturing, ask the server to flush the final
  // transcript, then close. A fallback timer guarantees the socket never lingers
  // open even if the server is slow or the final message is lost.
  const stop = useCallback(() => {
    const ws = wsRef.current;
    stopSilenceWatch();
    try { if (mrRef.current && mrRef.current.state !== 'inactive') mrRef.current.stop(); } catch { /* ignore */ }
    try { streamRef.current?.getTracks().forEach(track => track.stop()); } catch { /* ignore */ }
    mrRef.current = null;
    streamRef.current = null;
    setStatus('idle');
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ done: true })); } catch { /* ignore */ }
      if (finishRef.current) clearTimeout(finishRef.current);
      finishRef.current = setTimeout(teardown, 6000); // close after final transcript, or force after 6s
    } else {
      teardown();
    }
  }, [teardown, stopSilenceWatch]);

  /**
   * The transcript as of right now, including the interim not yet finalised.
   * Callers confirm in the same tick they call stop(), where the `transcript`
   * state is still the previous render's value.
   */
  const getTranscript = useCallback(
    () => (accRef.current + interimRef.current).trim(),
    [],
  );

  /**
   * «پایان» for the server path: stop capturing, flush the last chunk, tell the
   * server the recording is over, and resolve with the FINAL transcript once it
   * arrives — or with what has arrived by `timeoutMs`. Never rejects.
   */
  const finishTranscript = useCallback((timeoutMs = FINAL_TRANSCRIPT_TIMEOUT_MS) => new Promise((resolve) => {
    // The recording is ending: nothing is left for the silence watch to end.
    stopSilenceWatch();
    const ws = wsRef.current;
    const mr = mrRef.current;
    const stream = streamRef.current;
    mrRef.current = null;
    streamRef.current = null;
    setStatus('idle');

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      try { if (mr && mr.state !== 'inactive') mr.stop(); } catch { /* ignore */ }
      try { stream?.getTracks().forEach(track => track.stop()); } catch { /* ignore */ }
      teardown();
      resolve((accRef.current + interimRef.current).trim());
      return;
    }

    finalResolveRef.current = resolve;
    const sendDone = () => sendChainRef.current.then(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ done: true })); } catch { /* ignore */ }
      }
    });
    if (mr && mr.state !== 'inactive') {
      mr.onstop = () => {
        try { stream?.getTracks().forEach(track => track.stop()); } catch { /* ignore */ }
        sendDone();
      };
      try { mr.stop(); } catch { sendDone(); }
    } else {
      try { stream?.getTracks().forEach(track => track.stop()); } catch { /* ignore */ }
      sendDone();
    }
    if (finishRef.current) clearTimeout(finishRef.current);
    finishRef.current = setTimeout(teardown, timeoutMs);
  }), [teardown, stopSilenceWatch]);

  const reset = useCallback(() => {
    accRef.current = '';
    interimRef.current = '';
    setTranscript('');
    setStatus('idle');
    setErrorDetail('');
  }, []);

  /**
   * Throw the recording away: close the socket WITHOUT «done». The BFF then cancels
   * the upstream stream (transcribeHandler.js), and a recording that had not yet
   * reached the AI service's first pass is never transcribed — or charged.
   */
  const cancel = useCallback(() => {
    accRef.current = '';
    interimRef.current = '';
    setTranscript('');
    setStatus('idle');
    setErrorDetail('');
    teardown();
  }, [teardown]);

  const start = useCallback(async ({ autoStop = false, meter = false } = {}) => {
    // Re-entry guard: if a socket is already connecting/open (or start() is
    // mid-flight), do nothing. The inline voice bar's auto-restart effect can call start()
    // again while the WS is still connecting (status not yet 'listening'); without
    // this guard that opens a second WebSocket.
    if (startingRef.current || (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED)) {
      return;
    }
    startingRef.current = true;
    const gen = genRef.current;
    setErrorDetail('');
    if (!window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
      setStatus('unsupported');
      startingRef.current = false;
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (gen !== genRef.current) return;
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') setStatus('denied');
      else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') { setStatus('error'); setErrorDetail(t('errors.voice.noMicrophone')); }
      else { setStatus('error'); setErrorDetail(err?.message || t('errors.voice.microphoneDenied')); }
      startingRef.current = false;
      return;
    }
    // Cancelled while the browser was asking for the microphone: release it, record nothing.
    if (gen !== genRef.current) {
      try { stream.getTracks().forEach(track => track.stop()); } catch { /* ignore */ }
      return;
    }
    streamRef.current = stream;

    const token = getToken() || '';
    const wsUrl = `${WS_BASE}/ws/transcribe?token=${encodeURIComponent(token)}`;
    // The clinic is NOT inferable here, so it travels with the socket exactly as it
    // travels with every HTTP request (design §A10); the BFF validates it against
    // membership. It does not decide who pays: server-side transcription bills the
    // PERSON who opened the socket (their verified sub), like every AI call.
    const activeClinic = getActiveClinicRef();
    const wsUrlWithClinic = activeClinic
      ? `${wsUrl}&clinic=${encodeURIComponent(activeClinic)}`
      : wsUrl;
    let ws;
    try {
      ws = new WebSocket(wsUrlWithClinic);
    } catch {
      setStatus('error'); setErrorDetail(t('errors.voice.serviceUnreachable'));
      teardown();
      return;
    }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      const mimeType = pickMime();
      let mr;
      try {
        mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      } catch {
        setStatus('error'); setErrorDetail(t('errors.voice.unsupported'));
        teardown();
        return;
      }
      mrRef.current = mr;
      mr.ondataavailable = (e) => {
        if (!e.data || e.data.size === 0) return;
        const blob = e.data;
        sendChainRef.current = sendChainRef.current
          .then(() => blob.arrayBuffer())
          .then((buf) => {
            if (ws.readyState === WebSocket.OPEN) { try { ws.send(buf); } catch { /* ignore */ } }
          })
          .catch(() => { /* a chunk that cannot be read is dropped, not fatal */ });
      };
      mr.start(500); // emit a chunk every 500ms
      startingRef.current = false;
      setStatus('listening');
      if (autoStop || meter) {
        const vad = watchSilence(stream, {
          detect: autoStop,
          onSilence: () => handlersRef.current.onSilence?.(),
          onNoSpeech: () => handlersRef.current.onNoSpeech?.(),
          onLevel: (level) => handlersRef.current.onLevel?.(level),
        });
        vadRef.current = vad;
        vad.ready.then((ok) => {
          if (!ok && vadRef.current === vad) {
            vadRef.current = null;
            vad.stop();
            // Only a recording that was to END on silence has lost something.
            if (autoStop) handlersRef.current.onAutoStopUnavailable?.();
          }
        });
      }
    };

    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : ''); } catch { return; }
      if (!msg) return;
      if (msg.error) { setStatus('error'); setErrorDetail(msg.error); teardown(); return; }
      if (typeof msg.text === 'string') {
        if (msg.is_final) {
          // Final message carries the FULL authoritative transcript — replace,
          // don't append (interim messages were deltas).
          const full = String(msg.text).trim();
          if (full) accRef.current = full + ' ';
          interimRef.current = '';
          onFinalSegment?.(full);
          setTranscript(accRef.current.trim());
          setStatus('idle');
          teardown(); // final received → close the socket now
        } else {
          interimRef.current = msg.text;
          setTranscript((accRef.current + msg.text).trim());
        }
      }
    };

    ws.onerror = () => { setStatus('error'); setErrorDetail(t('errors.voice.serviceConnection')); teardown(); };
    ws.onclose = (ev) => {
      if (finishRef.current) { clearTimeout(finishRef.current); finishRef.current = null; }

      // A SERVER REFUSAL, not a normal end of stream. The BFF closes with a
      // 4xxx code before any audio is transcribed — 4001 when the token is bad,
      // 4003 when the account has no clinic to bill the پارسه to.
      //
      // Ignoring the code left the worst possible state: the socket closes, the
      // status flips listening → idle, and because `teardown()` is never called
      // on this path the MediaRecorder and the microphone TRACKS keep running.
      // The browser's recording indicator stayed lit, the user saw no message
      // at all, and every frame was silently dropped by `ondataavailable`'s
      // readyState guard. It looked like the feature was working.
      const REFUSALS = { 4001: 'errors.voice.serviceConnection', 4003: 'errors.voice.noClinic' };
      if (ev?.code >= 4000) {
        setStatus('error');
        setErrorDetail(t(REFUSALS[ev.code] || 'errors.voice.refused'));
        teardown();
        return;
      }

      // A normal close while «پایان» is still waiting: settle it with what came.
      if (finalResolveRef.current) teardown();
      setStatus(prev => (prev === 'listening' ? 'idle' : prev));
    };
  }, [continuous, onFinalSegment, teardown, t]);

  useEffect(() => () => teardown(), [teardown]);

  return {
    status,
    transcript,
    errorDetail,
    start,
    stop,
    reset,
    cancel,
    getTranscript,
    finishTranscript,
    isSupported: typeof window !== 'undefined' && !!window.MediaRecorder,
  };
}
